import { useCallback, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import { CENTER_OF_EXCELLENCE } from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/types'
import { IS_PRODUCTION } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { COMMON_MIXPANEL_PAGE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { useLyraIntl } from '@lyrahealth-inc/shared-app-logic/src/features/intl/hooks/useLyraIntl'
import { SentryLogLevelInput } from '@lyrahealth-inc/shared-app-logic/src/features/sentry/utils'
import { useIsWellnessToolkitAvailable as useIsWellnessToolkitAvailableShared } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/hooks/useIsWellnessToolkitAvailable'
import { TOOLKIT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/types'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/productType/productTypeContext'

import { useManagerCoachingSeatEligibility } from './useManagerCoachingSeatEligibility'
import {
  getDisableSelfCareTopics,
  getHideChildSearchCTA,
  getHideDependentField,
  getIsCoachingEnabled,
  getIsCoachingForParentsEnabled,
  getIsTherapyEnabled,
  getLibraryDisabled,
} from '../../data/customer/customerSelectors'
import { getEpisodes } from '../../data/lyraTherapy/lyraTherapySelectors'
import {
  getId,
  getIsUserInternational,
  getIsUserLoggedIn,
  getIsUserTeen,
  getUserCountryName,
  getUserDisplayLanguageOrDefault,
  getUserDOB,
} from '../../data/user/userSelectors'
import { postSpeculativeSearch } from '../../features/triage/data/triageActions'
import { getRiskFactors, getSearches } from '../../features/triage/data/triageSelectors'
import { LOG_CATEGORY, logToSentry } from '../utils/userUtils'

export const useIsWellnessToolkitAvailable = (toolkit: TOOLKIT_TYPE, page?: COMMON_MIXPANEL_PAGE) => {
  const {
    isFamilyHubEnabled,
    isCoachingEntryPointEnabled,
    isInternationalLifestyleCoachingEnabled,
    parentToolKitEntryPointEnabled,
    isSpeculativeFilterEnabled,
    speculativeSearchThresholds,
    isCriticalIncidentSupportToolkitEnabled,
    isNeurodiversityCOEEnabled,
    isManagerHubEnabled,
  } = useFlags()
  const { isLyraSelect } = useContext(ProductTypeContext)

  const isEssentialsEnabled = !useSelector(getLibraryDisabled)
  const isCoachingEnabled = useSelector(getIsCoachingEnabled)
  const isTherapyEnabled = useSelector(getIsTherapyEnabled)
  const isCoachingForParentsEnabled = useSelector(getIsCoachingForParentsEnabled)
  const hideChildSearchCTA = useSelector(getHideChildSearchCTA)
  const hideDependentField = useSelector(getHideDependentField)
  const isUserTeen = useSelector(getIsUserTeen)
  const isInternationalUser = useSelector(getIsUserInternational)
  const lyraId = useSelector(getId)
  const triageSearches = useSelector(getSearches)
  const triageSearchesMemoized = useMemo(() => {
    return triageSearches?.toJS() ?? []
  }, [triageSearches])
  const riskFactors = useSelector(getRiskFactors)
  const riskFactorsMemoized = useMemo(() => {
    return riskFactors?.toJS()
  }, [riskFactors])
  const episodes = useSelector(getEpisodes)
  const episodesMemoized = useMemo(() => {
    return episodes?.toJS() ?? []
  }, [episodes])
  const isUserLoggedIn = useSelector(getIsUserLoggedIn)
  const userDOB = useSelector(getUserDOB)
  const userCountryName = useSelector(getUserCountryName)
  const userDisplayLanguage = useSelector(getUserDisplayLanguageOrDefault)
  const { activeLanguage } = useLyraIntl()
  const displayLanguage = isUserLoggedIn ? userDisplayLanguage : activeLanguage
  const location = useLocation()
  const disableSelfCareTopics = useSelector(getDisableSelfCareTopics)

  const logger = useCallback(
    (data: { message: string; type: SentryLogLevelInput }) => {
      logToSentry(IS_PRODUCTION ? LOG_CATEGORY.PROD_DEBUG : LOG_CATEGORY.STAGING_DEBUG, { userId: lyraId, ...data })
    },
    [lyraId],
  )

  const { seatEligibility: managerCoachingSeatEligibility } = useManagerCoachingSeatEligibility({
    shouldFetch: toolkit === CENTER_OF_EXCELLENCE.MANAGER_HUB,
  })

  return useIsWellnessToolkitAvailableShared({
    route: location.pathname,
    toolkit,
    page,
    lyraId,
    isUserTeen,
    triageSearches: triageSearchesMemoized,
    isCoachingEnabled,
    isTherapyEnabled,
    isCoachingForParentsEnabled,
    riskFactors: riskFactorsMemoized,
    episodes: episodesMemoized,
    isUserLoggedIn,
    isInternationalUser,
    postSpeculativeSearch,
    userDOB,
    userCountryName,
    isEssentialsEnabled,
    hideDependentField,
    hideChildSearchCTA,
    isFamilyHubEnabled,
    isCoachingEntryPointEnabled,
    isInternationalLifestyleCoachingEnabled,
    isCoachingForParentsEntryPointEnabled: parentToolKitEntryPointEnabled,
    isCriticalIncidentSupportToolkitEnabled,
    isNeurodiversityCOEEnabled,
    isManagerHubEnabled,
    displayLanguage,
    isSpeculativeFilterEnabled,
    speculativeSearchThresholds,
    logger,
    isLyraSelect,
    disableSelfCareTopics,
    managerCoachingSeatEligibility,
  })
}
